import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCommonInfo, getPAGAA000, postPAHAD010 } from '../../../services/Api';
import Checkbox from '../../../shared/components/Checkbox';
import Dropdown from '../../../shared/components/Dropdown';
import Nav from '../../../shared/components/Nav';
import { MENU_CUSTOMER_SERVICE_CENTER, MOBIPHONE_OPTIONS, PHONE_OPTIONS } from '../../../shared/constants/app';
import { decodeToken, handleFocus, shouldDisplayMenu, updateMenuVisibility } from '../../../utils';

const PAHAD010 = () => {
    const navigate = useNavigate();
    const [tokenInfo, setTokenInfo] = useState({});
    const login = useSelector(({ auth }) => auth.login);
    const token = login.token;
    const isLoggedIn = login.loggedIn;
    const [payload, setPayload] = useState({ emailDomain: 'zdirect', mobileNoPart1: '010' });
    const [acceptRole, setAcceptRole] = useState(false);
    const [customerServiceMenu, setCustomerServiceMenu] = useState([]);
    const [menuData, setMenuData] = useState([]);

    useEffect(() => {
        getPAGAA000()
            .then(({ data }) => {
                const updatedCustomerServiceMenu = updateMenuVisibility(data, MENU_CUSTOMER_SERVICE_CENTER);
                setCustomerServiceMenu(updatedCustomerServiceMenu);
                setMenuData(data);
            })
            .catch((error) => console.log(error));

        if (!isLoggedIn) {
            return;
        }
        setTokenInfo(decodeToken(token));

        getCommonInfo()
            .then(({ data }) => {
                setPayload({
                    nameKor: data.nameKor,
                    bizCompany: data.bizCompany,
                    email: data.email,
                    telNo: data.telNo || data.mobileNo,
                });

                if (data.email) {
                    const [emailPart1, emailPart2] = data.email.split('@');
                    const domainOptions = getOptions('EmailDomain').map((option) => option.value);
                    const emailDomain = domainOptions.includes(emailPart2) ? emailPart2 : 'zdirect';
                    setPayload((prev) => ({ ...prev, emailPart1, emailPart2, emailDomain }));
                }
                if (data.telNo.endsWith('--') || data.telNo === '' || data.telNo === null) {
                    const [mobileNoPart1, mobileNoPart2, mobileNoPart3] = data.mobileNo.split('-');
                    setPayload((prev) => ({ ...prev, mobileNoPart1, mobileNoPart2, mobileNoPart3 }));
                } else {
                    const [mobileNoPart1, mobileNoPart2, mobileNoPart3] = data.telNo.split('-');
                    setPayload((prev) => ({ ...prev, mobileNoPart1, mobileNoPart2, mobileNoPart3 }));
                }
            })
            .catch((error) => console.log(error));
        window.scrollTo(0, 0);
        // eslint-disable-next-line
    }, []);

    const getOptions = (name) => {
        const options = {
            EmailDomain: [
                { id: 'EmailDomain_naver.com', option: 'naver.com', value: 'naver.com' },
                { id: 'EmailDomain_gmail.com', option: 'gmail.com', value: 'gmail.com' },
                { id: 'EmailDomain_daum.com', option: 'daum.com', value: 'daum.com' },
                { id: 'EmailDomain_hanmail.com', option: 'hanmail.com', value: 'hanmail.com' },
                { id: 'EmailDomain_outlook.com', option: 'outlook.com', value: 'outlook.com' },
                { id: 'EmailDomain_nate.com', option: 'nate.com', value: 'nate.com' },
                { id: 'EmailDomain_zdirect', option: '직접입력', value: 'zdirect' },
            ],
        };

        return options[name];
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'mobileNoPart2' || name === 'mobileNoPart3') {
            if (!/^\d*$/.test(value)) {
                return;
            }
        }
        setPayload((prevData) => {
            const newData = {
                ...prevData,
                [name]: value,
            };

            if (name === 'emailPart2') {
                const domainOptions = getOptions('EmailDomain').map((option) => option.value);
                if (domainOptions.includes(value)) {
                    newData.emailDomain = value;
                }
            }

            if (['emailPart1', 'emailPart2'].includes(name)) {
                newData.email = `${newData.emailPart1 || ''}@${newData.emailPart2 || ''}`;
            }

            if (['mobileNoPart1', 'mobileNoPart2', 'mobileNoPart3'].includes(name)) {
                newData.telNo = `${newData.mobileNoPart1 || ''}-${newData.mobileNoPart2 || ''}-${newData.mobileNoPart3 || ''}`;
            }

            return newData;
        });
    };

    const handleEmailDomainChange = (selectedValue) => {
        const updatedEmailPart2 = selectedValue === 'zdirect' ? '' : selectedValue;
        const updatedEmail = `${payload.emailPart1 || ''}@${updatedEmailPart2}`;
        setPayload((prevData) => ({
            ...prevData,
            emailDomain: selectedValue,
            emailPart2: updatedEmailPart2,
            email: updatedEmail,
        }));
    };

    const toggleAcceptRole = (e) => {
        setAcceptRole(e.target.checked);
    };

    const handleSubmit = () => {
        // 1. Validation: 이름
        if (!payload.nameKor) {
            window.alert('이름을 입력해 주세요.');
            handleFocus('nameKor');
            return;
        }

        // 2. Validation: 근무처(소속)
        if (!payload.bizCompany) {
            window.alert('근무처(소속)을 입력해 주세요.');
            handleFocus('bizCompany');
            return;
        }
        if (payload.bizCompany.length > 100) {
            window.alert('근무처(소속) 최대 100 글자까지만 입력이 가능합니다.');
            handleFocus('bizCompany');
            return;
        }

        // 3. Validation: 이메일
        const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!payload.email || !regexEmail.test(payload.email)) {
            window.alert('이메일을 입력해 주세요.');
            if (!payload.emailPart1) {
                handleFocus('emailPart1');
            } else if (!payload.emailPart2) {
                handleFocus('emailPart2');
            }
            return;
        }

        // 4. Validation: 연락처 - trim để loại bỏ khoảng trắng
        const part1 = (payload.mobileNoPart1 || '').trim();
        const part2 = (payload.mobileNoPart2 || '').trim();
        const part3 = (payload.mobileNoPart3 || '').trim();

        if (!part1 || !part2 || !part3) {
            window.alert('연락처를 입력해 주세요.');
            if (!part1) handleFocus('mobileNoPart1');
            else if (!part2) handleFocus('mobileNoPart2');
            else handleFocus('mobileNoPart3');
            return;
        }

        // 4-1. Validation: 연락처 지역번호 유효성
        const validOptions = new Set([
            ...MOBIPHONE_OPTIONS.map((option) => String(option.value)),
            ...PHONE_OPTIONS.map((option) => String(option.value)),
        ]);

        if (!validOptions.has(part1)) {
            window.alert('잘못된 번호입니다. 다시 확인해 주세요.');
            handleFocus('mobileNoPart1');
            return;
        }

        // 4-2. Validation: part2, part3 
        if (!/^\d+$/.test(part2) || !/^\d+$/.test(part3)) {
            window.alert('연락처는 숫자만 입력 가능합니다.');
            handleFocus('mobileNoPart2');
            return;
        }

        const isMobile = MOBIPHONE_OPTIONS.map((o) => String(o.value)).includes(part1);
        const isSeoul = part1 === '02';

        let validPart2Lengths;
        let validPart3Lengths;

        if (isMobile) {
            // Mobile: 010-XXXX-XXXX
            validPart2Lengths = [4];
            validPart3Lengths = [4];
        } else if (isSeoul) {
            // Seoul landline: 02-XXX-XXXX  02-XXXX-XXXX
            validPart2Lengths = [3, 4];
            validPart3Lengths = [4];
        } else {
            // Landline tỉnh: 0XX-XXX-XXXX  0XX-XXXX-XXXX
            validPart2Lengths = [3, 4];
            validPart3Lengths = [4];
        }

        if (!validPart2Lengths.includes(part2.length) || !validPart3Lengths.includes(part3.length)) {
            window.alert('연락처 형식이 올바르지 않습니다.');
            handleFocus('mobileNoPart2');
            return;
        }

        const telNo = `${part1}-${part2}-${part3}`;

        // 5. Validation: 제목
        if (!payload.title) {
            window.alert('제목을 입력해 주세요.');
            handleFocus('title');
            return;
        }
        if (payload.title.length > 200) {
            window.alert('제목 최대 200 글자까지만 입력이 가능합니다.');
            handleFocus('title');
            return;
        }

        // 6. Validation: 내용
        if (!payload.content) {
            window.alert('내용을 입력해 주세요.');
            handleFocus('contentArea');
            return;
        }

        // 7. Validation: 개인정보 동의
        if (!acceptRole) {
            window.alert(
                '이메일문의 답변처리를 위해 필요한 최소한의 개인정보이므로 동의를해 주셔야 서비스를 이용할 수 있습니다.',
            );
            handleFocus('acceptYNForcus');
            return;
        }

        // 8. payload 정리 (불필요한 필드 제거 + telNo 최종값 반영)
        const submitPayload = { ...payload, telNo };
        delete submitPayload.emailPart1;
        delete submitPayload.emailPart2;
        delete submitPayload.emailDomain;
        delete submitPayload.mobileNoPart1;
        delete submitPayload.mobileNoPart2;
        delete submitPayload.mobileNoPart3;

        // 9. API 호출
        postPAHAD010(submitPayload)
            .then(() => {
                window.alert(
                    `이메일 문의 내용이 정상적으로 등록되었습니다.\n문의 주신 사항에 대해 확인 후 정확한 답변 드리도록 하겠습니다.\n감사합니다.`,
                );
                navigate('/');
            })
            .catch((error) => {
                console.error('이메일 문의 등록 중 오류 발생:', error);
                window.alert('문의 등록 중 오류가 발생했습니다. 다시 시도해 주세요.');
            });
    };

    return (
        <div id="content">
            <div id="request-email">
                <div className="container">
                    <Nav menu={customerServiceMenu} />
                    <h1 className="title">이메일 문의</h1>
                    {shouldDisplayMenu('pahae010', menuData) && (
                        <div className="request">
                            <span>
                                덴탈잡은 자주 묻는 질문에 대한 답변을 제공하고 있습니다.{' '}
                                <em style={{ color: '#0f5ac2', fontStyle: 'normal' }}>문의 전에 꼭 확인해 주세요.</em>
                            </span>
                            <button onClick={() => navigate('/pahae010')}>자주 묻는 질문 보기</button>
                        </div>
                    )}

                    <ul className="form table-horizontal">
                        <li>
                            <div className="table-title require">이름</div>
                            <div className="table-content">
                                <input
                                    className="wd336"
                                    type="text"
                                    name="nameKor"
                                    value={payload.nameKor || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </li>
                        <li className="custom-title">
                            <div className="table-title require">
                                <span>근무처</span>
                                <span>(소속)</span>
                            </div>
                            <div className="table-content">
                                <input
                                    className="wd336"
                                    type="text"
                                    name="bizCompany"
                                    value={payload.bizCompany || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </li>
                        <li className="row-has-require-bottom h80">
                            <div className="table-title require">이메일</div>
                            <div className="table-content form-email">
                                <input
                                    className="wd193"
                                    type="text"
                                    name="emailPart1"
                                    value={payload.emailPart1 || ''}
                                    onChange={handleInputChange}
                                />
                                <span>@</span>
                                <input
                                    className="wd157"
                                    type="text"
                                    name="emailPart2"
                                    value={payload.emailPart2 || ''}
                                    onChange={handleInputChange}
                                    disabled={payload.emailDomain !== 'zdirect'}
                                />

                                <div className="form-select-container select-phone wd158">
                                    <Dropdown
                                        placeholder=""
                                        options={getOptions('EmailDomain')}
                                        value={payload.emailDomain}
                                        name="emailDomain"
                                        onChange={handleEmailDomainChange}
                                    />
                                </div>
                            </div>
                        </li>
                        <li>
                            <div className="table-title require">연락처</div>
                            <div className="table-content content-form form-phone">
                                <input
                                    type="text"
                                    className="wd120 text-center"
                                    name="mobileNoPart1"
                                    value={payload.mobileNoPart1 || ''}
                                    onChange={handleInputChange}
                                />
                                <span>-</span>
                                <input
                                    type="text"
                                    maxLength={4}
                                    className="wd120 text-center"
                                    name="mobileNoPart2"
                                    value={payload.mobileNoPart2 || ''}
                                    onChange={handleInputChange}
                                />
                                <span>-</span>
                                <input
                                    type="text"
                                    maxLength={4}
                                    className="wd120 text-center"
                                    name="mobileNoPart3"
                                    value={payload.mobileNoPart3 || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </li>
                        <li>
                            <div className="table-title require">제목</div>
                            <div className="table-content">
                                <input type="text" className="input-title" name="title" onChange={handleInputChange} />
                            </div>
                        </li>
                        <li className="auto">
                            <div className="table-title require">내용</div>
                            <div className="table-content">
                                <textarea type="text" name="content" id="contentArea" onChange={handleInputChange} />
                            </div>
                        </li>
                        {tokenInfo?.memberID && (
                            <li>
                                <div className="table-title">아이디</div>
                                <div className="table-content">
                                    <input
                                        type="text"
                                        className="wd336"
                                        name="memberID"
                                        style={{ background: '#eee', pointerEvents: 'none' }}
                                        value={tokenInfo?.memberID}
                                        readOnly
                                    />
                                </div>
                            </li>
                        )}
                    </ul>
                    <div className="d-flex align-item-center">
                        <Checkbox
                            options={[{ id: 'acceptYN', value: true, label: '개인정보 수집 및 이용 동의합니다.' }]}
                            checkedValues={acceptRole ? [true] : []}
                            onChange={toggleAcceptRole}
                        />
                        <strong id="acceptYNForcus">[필수]</strong>
                    </div>
                    <div className="table table-content">
                        <table>
                            <thead>
                                <tr>
                                    <th className="column1">목적</th>
                                    <th className="column2">항목</th>
                                    <th className="column3">보유기간</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="column1" data-label="목적">
                                        이용자식별 및 본인여부 확인
                                    </td>
                                    <td className="column2" data-label="항목">
                                        이름, 근무처(소속), 아이디(로그인 회원인 경우만)
                                    </td>
                                    <td className="column3" data-label="보유기간" rowSpan={2}>
                                        <ul>
                                            <li>비회원: 접수일부터 3개월까지</li>
                                            <li>회원: 탈퇴시까지</li>
                                        </ul>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="column1" data-label="목적">
                                        고객 상담 처리
                                    </td>
                                    <td className="column2" data-label="항목">
                                        이메일, 연락처
                                    </td>
                                </tr>
                                <tr>
                                    <td data-label="보유기간">
                                        <ul>
                                            <li>비회원: 접수일부터 3개월까지</li>
                                            <li>회원: 탈퇴시까지</li>
                                        </ul>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p className="require-form">
                            ※ 이메일문의 답변처리를 위해 필요한 최소한의 개인정보이므로 동의를 해 주셔야 서비스를 이용할
                            수 있습니다.
                        </p>
                        <div className="button-group">
                            <button className="button button-blue radius50" onClick={handleSubmit}>
                                문의하기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PAHAD010;